import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Plus, FileText, AlertCircle, LogOut, Settings, User, Activity, Shield, Briefcase, MoreHorizontal, LifeBuoy, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePlatformConfig } from '../context/PlatformConfigContext';
import { toast } from 'sonner';
import TrustTradeLogo from './TrustTradeLogo';
import api from '../utils/api';
import { buildUserActivityFeed, getUnreadActivityCount, markActivitySeen } from '../utils/transactionActivity';
import { trackStartTransaction } from '../utils/analytics';
import FeedbackButton from './FeedbackButton';
import { V, VDark } from './designTokens';
import { Product } from './productTokens';

// Moved to ./designTokens.js (kept dependency-free so pure presentation-logic
// modules and their unit tests can import colors without pulling in
// react-router-dom via this file). Re-exported below so every existing
// `import { V } from '../components/DashboardLayout'` keeps working.
export { V, VDark, Product };

function DashboardLayout({ children, user: userProp, loading = false, darkMain = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user: authUser } = useAuth();
  const { config: platformConfig } = usePlatformConfig();
  const user = userProp || authUser;
  const [unreadActivityCount, setUnreadActivityCount] = useState(0);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const mobileSheetRef = useRef(null);
  // Keep legacy token exports stable while restoring the original dark customer palette.
  const C = Product;

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap';
    document.head.appendChild(link);
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user) return undefined;

    async function fetchActivityCount() {
      try {
        const [transactionsRes, disputesRes] = await Promise.all([
          api.get('/transactions'),
          api.get('/disputes').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const events = buildUserActivityFeed(transactionsRes.data || [], {
          user,
          disputes: disputesRes.data || [],
          limit: 40,
          activeFirst: false,
        });
        setUnreadActivityCount(getUnreadActivityCount(events));
      } catch {
        if (!cancelled) setUnreadActivityCount(0);
      }
    }

    fetchActivityCount();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (location.pathname === '/activity') {
      markActivitySeen();
      setUnreadActivityCount(0);
    }
  }, [location.pathname]);

  // Close the mobile "More" sheet whenever the route changes (link click,
  // back/forward navigation, etc.) so it never stays open over a new page.
  useEffect(() => {
    setMobileMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMoreOpen) return undefined;
    const previousFocus = document.activeElement;
    const sheet = mobileSheetRef.current;
    const controls = () => [...sheet.querySelectorAll('a[href], button:not(:disabled)')];
    controls()[0]?.focus();
    const onKeyDown = event => {
      if (event.key === 'Escape') setMobileMoreOpen(false);
      if (event.key !== 'Tab') return;
      const items = controls();
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    sheet.addEventListener('keydown', onKeyDown);
    return () => { sheet.removeEventListener('keydown', onKeyDown); previousFocus?.focus(); };
  }, [mobileMoreOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Signed out');
      navigate('/login');
    } catch {
      toast.error('Failed to sign out');
    }
  };

  const handleNavItemClick = (item) => {
    if (item.path === '/transactions/new') {
      trackStartTransaction({ source: 'dashboard_nav' });
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard',       path: '/dashboard' },
    { icon: Plus,            label: 'New Transaction', path: '/transactions/new', highlight: true },
    ...(platformConfig.milestones_enabled ? [{ icon: Briefcase, label: 'Milestone Deals', path: '/smart-deals' }] : []),
    { icon: FileText,        label: 'My Transactions', path: '/transactions', end: true },
    { icon: AlertCircle,     label: 'Disputes',        path: '/disputes-dashboard' },
    { icon: Activity,        label: 'Live Activity',   path: '/activity' },
    { icon: User,            label: 'My Profile',      path: '/profile' },
    { icon: Shield,          label: 'Scam Watch',      path: '/blog' },
  ];

  if (user?.is_admin) {
    navItems.push({ icon: Settings, label: 'Admin Dashboard', path: '/admin' });
  }

  // Mobile bottom nav is intentionally narrower than the desktop sidebar:
  // Home / New / Transactions stay one tap away, everything else (plus
  // Help/support and Sign out) lives behind "More" so the bar never crowds
  // or scrolls on small screens.
  const mobilePrimaryItems = [
    { icon: LayoutDashboard, label: 'Home',         path: '/dashboard' },
    { icon: Plus,            label: 'New',          path: '/transactions/new' },
    { icon: FileText,        label: 'Transactions', path: '/transactions', end: true },
  ];
  const mobileMoreItems = [
    ...(platformConfig.milestones_enabled ? [{ icon: Briefcase, label: 'Milestone Deals', path: '/smart-deals' }] : []),
    { icon: AlertCircle, label: 'Disputes',    path: '/disputes-dashboard' },
    { icon: Activity,    label: 'Activity',    path: '/activity' },
    { icon: User,        label: 'Profile',     path: '/profile' },
    { icon: Shield,      label: 'Scam Watch',  path: '/blog' },
  ];
  if (user?.is_admin) {
    mobileMoreItems.push({ icon: Settings, label: 'Admin Dashboard', path: '/admin' });
  }
  const mobileMoreActive = mobileMoreItems.some(item => location.pathname.startsWith(item.path));

  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '??';

  const pageBackground = C.bgGlow;

  return (
    <div className="tt-dark-ui tt-product" style={{ display: 'flex', minHeight: '100vh', background: pageBackground, backgroundSize: 'auto', fontFamily: C.sans, color: C.text }}>

      <a className="tt-skip-link" href="#main-content">Skip to content</a>
      {/* ── Global styles ── */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }

        body {
          background: ${C.bg};
          color: ${C.text};
          font-family: ${C.sans};
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        @keyframes vaultProgress {
          0%   { left: -40%; width: 40%; }
          60%  { left: 60%;  width: 40%; }
          100% { left: 100%; width: 40%; }
        }
        @keyframes vaultPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
        @keyframes vaultSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes vaultFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: none; }
        }

        /* Scrollbar */
        ::-webkit-scrollbar        { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track  { background: ${C.bg}; }
        ::-webkit-scrollbar-thumb  { background: ${C.border}; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: ${C.dim}; }

        /* Sidebar layout */
        .vault-sidebar {
          width: 220px;
          position: fixed;
          top: 0; left: 0;
          height: 100vh;
          display: flex;
          flex-direction: column;
          background: ${C.surface};
          border-right: 1px solid ${C.border};
          box-shadow: 10px 0 30px rgba(15, 30, 53, 0.04);
          z-index: 40;
          overflow: hidden;
        }
        .vault-main {
          margin-left: 220px;
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        @media (max-width: 1023px) {
          .vault-sidebar { display: none !important; }
          .vault-main { margin-left: 0 !important; }
        }

        /* Vault table hover */
        .vault-tr:hover td { background: ${C.hover} !important; }

        /* Vault row hover (non-table) */
        .vault-row:hover { background: ${C.hover} !important; }

        /* Vault button hover — neon outline */
        .vault-btn {
          transition: border-color 0.12s, color 0.12s, background 0.12s !important;
        }
        .vault-btn:hover:not(:disabled) {
          border-color: ${C.accent} !important;
          color: ${C.accent} !important;
        }
        .vault-btn-primary:hover:not(:disabled) {
          background: rgba(47,129,244,0.08) !important;
          border-color: ${C.accent} !important;
          color: ${C.accent} !important;
        }
        .vault-btn-danger:hover:not(:disabled) {
          border-color: ${C.error} !important;
          color: ${C.error} !important;
        }
        .vault-btn-success:hover:not(:disabled) {
          border-color: ${C.success} !important;
          color: ${C.success} !important;
        }

        /* Input focus */
        .vault-input:focus {
          outline: none;
          border-color: ${C.accent} !important;
          box-shadow: 0 0 0 2px rgba(0,209,255,0.12);
        }
        .vault-mobile-nav {
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }
        .vault-mobile-nav::-webkit-scrollbar { display: none; }
        /* The <nav> below sets display:flex via inline style, which (being
           inline) always outranks the lg:hidden utility class regardless of
           viewport. !important is the only thing that can still win against
           an inline declaration, so this is what actually hides the bar on
           desktop. */
        @media (min-width: 1024px) {
          .vault-mobile-nav { display: none !important; }
        }
        .vault-mobile-link { min-width: 64px; flex: 0 0 auto; }
      `}</style>

      {/* ── Linear progress bar ── */}
      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          height: 2, zIndex: 9999,
          background: C.border, overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 0, height: '100%', width: '40%',
            background: `linear-gradient(90deg, transparent, ${C.accent}, transparent)`,
            animation: 'vaultProgress 1.4s ease-in-out infinite',
          }} />
        </div>
      )}

      {/* ── Sidebar (desktop) ── */}
      <aside className="vault-sidebar">
        {/* Logo */}
        <div style={{ padding: '14px 14px 12px', borderBottom: `1px solid ${C.border}` }}>
          <NavLink to="/dashboard" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <TrustTradeLogo dark size="medium" showText />
          </NavLink>
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '12px 10px 8px', overflowY: 'auto', minHeight: 0 }}>
          <div style={{ padding: '0 6px 8px' }}>
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: C.dim,
            }}>
              Menu
            </span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.end}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleNavItemClick(item)}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 9,
                    padding: '11px 10px', marginBottom: 4,
                    fontSize: 13, fontWeight: isActive ? 600 : 400,
                    color: item.highlight ? '#16181B' : isActive ? C.text : C.sub,
                    background: item.highlight ? C.accent : isActive ? 'rgba(47,129,244,0.08)' : 'transparent',
                    borderLeft: `2px solid ${isActive ? C.accent : 'transparent'}`,
                    borderRadius: 10,
                    textDecoration: 'none',
                    transition: 'color 0.1s, background 0.1s',
                    cursor: 'pointer',
                    letterSpacing: '-0.01em',
                  })}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        size={13}
                        color={item.highlight ? '#16181B' : isActive ? C.accent : C.dim}
                        style={{ flexShrink: 0 }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {item.label}
                      </span>
                      {item.label === 'Live Activity' && unreadActivityCount > 0 && (
                        <span
                          data-testid="nav-notification-badge"
                          title={`${unreadActivityCount} unread transaction, payout, or dispute updates`}
                          style={{
                            minWidth: 18,
                            height: 18,
                            padding: '0 5px',
                            borderRadius: 999,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: C.accent,
                            color: '#061421',
                            fontSize: 9,
                            fontWeight: 800,
                          }}
                        >
                          {unreadActivityCount > 99 ? '99+' : unreadActivityCount}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User card + logout */}
        <div style={{ padding: '10px 10px 16px', borderTop: `1px solid ${C.border}` }}>
          <div
            onClick={() => navigate('/profile')}
            className="vault-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 9,
              padding: '8px 10px', marginBottom: 8,
              border: `1px solid ${C.border}`, borderRadius: 3,
              cursor: 'pointer', background: 'transparent',
            }}
          >
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user?.name}
                style={{ width: 28, height: 28, borderRadius: 2, border: `1px solid ${C.border}`, flexShrink: 0, objectFit: 'cover' }}
              />
            ) : (
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(47,129,244,0.08)',
                border: `1px solid rgba(47,129,244,0.20)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: C.accent,
              }}>
                {userInitials}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                data-testid="user-name"
                style={{
                  fontSize: 12, fontWeight: 600, color: C.text,
                  margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {user?.name || 'User'}
              </p>
              <p style={{
                fontSize: 11, color: C.sub, margin: 0,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {user?.email}
              </p>
            </div>
          </div>

          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 12px', borderRadius: 4,
              border: `1px solid rgba(255,59,48,0.4)`,
              background: 'rgba(255,59,48,0.08)',
              color: C.error,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: C.sans,
              transition: 'background 0.12s, border-color 0.12s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255,59,48,0.16)';
              e.currentTarget.style.borderColor = C.error;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(255,59,48,0.08)';
              e.currentTarget.style.borderColor = 'rgba(255,59,48,0.4)';
            }}
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="vault-main">
        {/* Mobile header */}
        <header
          className="flex lg:hidden"
          style={{
            height: 56, background: C.surface,
            borderBottom: `1px solid ${C.border}`,
            alignItems: 'center', justifyContent: 'space-between',
            padding: '0 16px',
            position: 'sticky', top: 0, zIndex: 30,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TrustTradeLogo dark size="small" showText />
          </div>
          <div>
            {user?.picture ? (
              <img src={user.picture} alt={user?.name} style={{ width: 26, height: 26, borderRadius: 2, border: `1px solid ${C.border}` }} />
            ) : (
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: 'rgba(47,129,244,0.08)', border: `1px solid rgba(47,129,244,0.20)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, color: C.accent,
              }}>
                {userInitials}
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: '28px', paddingBottom: 88, background: darkMain ? 'transparent' : C.bg, color: C.text }} className="vault-content lg:pb-6" id="main-content">
          {children}
        </main>
      </div>

      {/* ── Mobile bottom nav: Home / New / Transactions / More ── */}
      <nav
        className="vault-mobile-nav lg:hidden"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
          background: C.surface, borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          height: 56, padding: '0 8px',
        }}
      >
        {mobilePrimaryItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={() => handleNavItemClick(item)}
            className="vault-mobile-link"
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            style={({ isActive }) => ({
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              padding: '6px 8px', textDecoration: 'none',
              color: isActive ? C.accent : C.sub,
              transition: 'color 0.1s',
            })}
          >
            <item.icon size={15} />
            <span style={{ fontSize: 10, fontWeight: 600 }}>
              {item.label}
            </span>
          </NavLink>
        ))}
        <button
          data-testid="mobile-nav-more"
          aria-expanded={mobileMoreOpen}
          aria-controls="mobile-more-sheet"
          onClick={() => setMobileMoreOpen(true)}
          className="vault-mobile-link"
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            padding: '6px 8px', background: 'none', border: 'none',
            cursor: 'pointer', color: mobileMoreActive ? C.accent : C.sub,
            transition: 'color 0.1s', position: 'relative',
          }}
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <MoreHorizontal size={15} />
            {unreadActivityCount > 0 && (
              <span
                data-testid="mobile-nav-notification-badge"
                title={`${unreadActivityCount} unread transaction, payout, or dispute updates`}
                style={{
                  position: 'absolute', right: -8, top: -7,
                  minWidth: 15, height: 15, padding: '0 4px',
                  borderRadius: 999, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: C.accent, color: '#061421',
                  fontSize: 8, fontWeight: 800,
                  border: `1px solid ${C.surface}`,
                }}
              >
                {unreadActivityCount > 9 ? '9+' : unreadActivityCount}
              </span>
            )}
          </span>
          <span style={{ fontSize: 10, fontWeight: 600 }}>
            More
          </span>
        </button>
      </nav>

      {/* ── Mobile "More" sheet: everything the bottom bar doesn't have room for ── */}
      {mobileMoreOpen && (
        <div
          className="lg:hidden"
          onClick={() => setMobileMoreOpen(false)}
          style={{
            // Above FeedbackButton (zIndex: 1001) so the floating launcher
            // button never floats above this full-screen overlay/sheet on
            // mobile.
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.5)',
          }}
        >
          <div
            ref={mobileSheetRef}
            id="mobile-more-sheet"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="More navigation"
            data-testid="mobile-more-sheet"
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0,
              background: C.surface, borderTop: `1px solid ${C.border}`,
              borderTopLeftRadius: 16, borderTopRightRadius: 16,
              padding: '8px 8px calc(env(safe-area-inset-bottom, 0px) + 8px)',
              maxHeight: '70vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 10px 6px' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>More</span>
              <button
                onClick={() => setMobileMoreOpen(false)}
                data-testid="mobile-more-close-btn"
                aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, padding: 6, display: 'flex' }}
              >
                <X size={16} />
              </button>
            </div>

            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {mobileMoreItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => handleNavItemClick(item)}
                    data-testid={`mobile-more-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    style={({ isActive }) => ({
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '11px 10px', borderRadius: 10,
                      color: isActive ? C.accent : C.text,
                      textDecoration: 'none', fontSize: 14, fontWeight: 500,
                    })}
                  >
                    <item.icon size={17} />
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.label === 'Activity' && unreadActivityCount > 0 && (
                      <span
                        style={{
                          minWidth: 18, height: 18, padding: '0 5px',
                          borderRadius: 999, display: 'inline-flex',
                          alignItems: 'center', justifyContent: 'center',
                          background: C.accent, color: '#061421',
                          fontSize: 9, fontWeight: 800,
                        }}
                      >
                        {unreadActivityCount > 99 ? '99+' : unreadActivityCount}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
              <li>
                <button
                  data-testid="mobile-more-help"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('trusttrade:open-support'));
                    setMobileMoreOpen(false);
                  }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '11px 10px', borderRadius: 10, border: 'none',
                    background: 'none', color: C.text, textAlign: 'left',
                    fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: C.sans,
                  }}
                >
                  <LifeBuoy size={17} />
                  <span>Help / Support</span>
                </button>
              </li>
              <li><button type="button" className="tt-mobile-feedback" onClick={() => { window.dispatchEvent(new CustomEvent('trusttrade:open-feedback')); setMobileMoreOpen(false); }}><LifeBuoy size={17} /> Send feedback</button></li>
            </ul>

            <div style={{ height: 1, background: C.border, margin: '4px 10px 8px' }} />

            <button
              data-testid="mobile-logout-btn"
              onClick={handleLogout}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '11px 10px', borderRadius: 10, border: 'none',
                background: 'none', color: C.error, textAlign: 'left',
                fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: C.sans,
              }}
            >
              <LogOut size={17} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
      <FeedbackButton />
    </div>
  );
}

export default DashboardLayout;
